/**
 * ZakazniciList — Phase 3.1 list workspace.
 *
 * Source: extrapolated from the radical artboard language. Reuses the workspace
 * shell calibrated by Vozidla (Phase 2.1): rail · list · inspector preview.
 *
 * Name/representative search in the editorial DataTable + Inspector pattern.
 * Filter chips on prodejce/typ are deferred — the customer schema doesn't carry
 * those fields today, so adding chips would be theatrical filtering.
 */

import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { AppRail } from '@/components/AppRail';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Chip } from '@/components/Chip';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Inspector, FieldRow } from '@/components/Inspector';
import { LoadMoreBar } from '@/components/LoadMoreBar';
import { Pill } from '@/components/Pill';
import { SearchInput } from '@/components/SearchInput';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { Subtabs } from '@/components/Subtabs';
import { Toolband } from '@/components/Toolband';
import { formatInt } from '@/lib/format';
import { useModuleAccess } from '@/lib/permissions';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { useCustomersInfiniteQuery, type Customer } from './api';
import { streetLine, cityLine } from './helpers';

const COLUMNS_TEMPLATE = 'minmax(0,1.4fr) 110px minmax(0,1fr) minmax(0,1fr) 110px';

export function ZakazniciList() {
  const navigate = useNavigate();
  const { isPhone } = useBreakpoint();
  const { can } = useModuleAccess();
  const canWrite = can('zakaznici', 'write');
  const canSprava = can('zakaznici', 'admin');
  const [search, setSearch] = useState('');
  const [icoOnly, setIcoOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Debounce the search box so the API isn't called per keystroke. Input
  // stays controlled (responsive caret); only `filters` reads the debounced
  // shape.
  const debouncedSearch = useDebouncedValue(search, 300);

  const filters = useMemo(
    () => ({
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    }),
    [debouncedSearch],
  );

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCustomersInfiniteQuery(filters);
  const customers = useMemo(
    () => data?.pages.flatMap((p) => p.customers) ?? [],
    [data],
  );
  const total = data?.pages[0]?.total ?? 0;

  const visible = useMemo(() => {
    if (!icoOnly) return customers;
    return customers.filter((c) => c.ico && c.ico.trim() !== '');
  }, [customers, icoOnly]);

  const selected =
    visible.find((c) => c.id_zakaznika === selectedId) ?? visible[0] ?? null;

  const counts = useMemo(() => {
    const acc = { total, withIco: 0, withEmail: 0 };
    for (const c of customers) {
      if (c.ico && c.ico.trim() !== '') acc.withIco += 1;
      if (c.email && c.email.trim() !== '') acc.withEmail += 1;
    }
    return acc;
  }, [customers, total]);

  return (
    <Shell rail={<AppRail active="zakaznici" />} inspector={<CustomerInspector customer={selected} />}>
      <Crown>
        <Breadcrumb segments={[{ label: 'Zákazníci' }, { label: 'Přehled' }]} />
        <Crown.Row className="mt-3">
          <DisplayHeading>Zákazníci</DisplayHeading>
          <CrownMeta
            items={[
              { label: 'Celkem', value: formatInt(counts.total) },
              { label: 'S IČO', value: formatInt(counts.withIco) },
              { label: 'S e-mailem', value: formatInt(counts.withEmail) },
            ]}
          />
        </Crown.Row>
        <Subtabs<'zakaznici' | 'vyhledavani' | 'sprava'>
          aria-label="Zákazníci — sekce"
          value="zakaznici"
          onChange={(next) => {
            if (next === 'vyhledavani') navigate({ to: '/zakaznici/vyhledavani' });
            else if (next === 'sprava') navigate({ to: '/zakaznici/sprava' });
          }}
          items={[
            { value: 'zakaznici', label: 'Zákazníci', count: counts.total },
            { value: 'vyhledavani', label: 'Vyhledávání' },
            ...(canSprava ? [{ value: 'sprava' as const, label: 'Správa' }] : []),
          ]}
        />
      </Crown>

      <Toolband>
        <SearchInput
          placeholder="Jméno klienta nebo zástupce…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          wrapperClassName="max-w-[360px] flex-1"
        />
        <Chip
          icon={<Icon name="filter" size={12} />}
          active={icoOnly}
          onClick={() => setIcoOnly((v) => !v)}
        >
          Pouze s IČO
        </Chip>
        <Toolband.Spacer />
        {canWrite && (
          <Button
            size="sm"
            variant="primary"
            icon={<Icon name="plus" size={12} />}
            onClick={() => navigate({ to: '/zakaznici/novy' })}
          >
            Nový zákazník
          </Button>
        )}
      </Toolband>

      <div
        data-testid="zakaznici-page"
        className="flex min-h-0 flex-1 flex-col px-8 pb-6 pt-3 max-sm:px-0 max-sm:pb-4 max-sm:pt-1"
      >
        <DataTable<Customer>
          aria-label="Seznam zákazníků"
          variant="bordered"
          columns={customerColumns()}
          data={isLoading ? [] : visible}
          getRowId={(c) => String(c.id_zakaznika)}
          selectedId={selected ? String(selected.id_zakaznika) : undefined}
          onRowClick={(c) =>
            // Phone (14.3): no inspector exists — tapping a card navigates to
            // the detail route. (No URL search state here to forward.)
            isPhone
              ? navigate({
                  to: '/zakaznici/$id',
                  params: { id: String(c.id_zakaznika) },
                })
              : setSelectedId(c.id_zakaznika)
          }
          empty={
            isLoading ? (
              <ListSkeleton />
            ) : isError ? (
              <EmptyState
                eyebrow="Chyba"
                title="Zákazníky se nepodařilo načíst"
                description="Zkuste obnovit stránku. Pokud problém trvá, kontaktujte správce."
              />
            ) : (
              <EmptyState
                eyebrow="Prázdné"
                title="Žádní zákazníci"
                description="Zkuste rozšířit hledání nebo přidejte nového zákazníka."
              />
            )
          }
          className="min-h-0 flex-1"
        />
      </div>
      <LoadMoreBar
        loaded={customers.length}
        total={total}
        hasMore={!!hasNextPage}
        loading={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        unit="zákazníků"
      />
    </Shell>
  );
}

function customerColumns(): DataTableColumn<Customer>[] {
  const widths = COLUMNS_TEMPLATE.split(' ');
  return [
    {
      id: 'name',
      priority: 'primary',
      header: 'Jméno',
      width: widths[0],
      cell: (c) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-semibold tracking-[-0.01em] text-ink">
            {c.jmeno || '—'}
          </span>
          {c.zastupce && (
            <span className="truncate text-[11px] text-ink-3">{c.zastupce}</span>
          )}
        </div>
      ),
    },
    {
      id: 'ico',
      priority: 'meta',
      header: 'IČ',
      width: widths[1],
      cell: (c) => (
        <span
          className={
            'font-mono text-[11.5px] tracking-[-0.02em] ' +
            (c.ico ? 'text-ink' : 'text-ink-3')
          }
        >
          {c.ico || '—'}
        </span>
      ),
    },
    {
      id: 'address',
      priority: 'secondary',
      header: 'Adresa',
      width: widths[2],
      cell: (c) => {
        const street = streetLine(c);
        const city = cityLine(c);
        return (
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-[12px] text-ink-2">{street || '—'}</span>
            <span className="truncate text-[11px] text-ink-3">{city || ''}</span>
          </div>
        );
      },
    },
    {
      id: 'contact',
      priority: 'meta',
      header: 'Kontakt',
      width: widths[3],
      cell: (c) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-[12px] text-ink-2">{c.email || '—'}</span>
          <span className="truncate font-mono text-[10.5px] tracking-[-0.02em] text-ink-3">
            {c.telefon || ''}
          </span>
        </div>
      ),
    },
    {
      id: 'dico',
      priority: 'meta',
      header: 'DIČ',
      width: widths[4],
      cell: (c) => (
        <span
          className={
            'font-mono text-[11px] tracking-[-0.02em] ' +
            (c.dico ? 'text-ink-2' : 'text-ink-3')
          }
        >
          {c.dico || '—'}
        </span>
      ),
    },
  ];
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-px p-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

function CustomerInspector({ customer }: { customer: Customer | null }) {
  const navigate = useNavigate();
  if (!customer) {
    return (
      <Inspector>
        <div className="grid h-full place-items-center px-6 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Vyberte zákazníka
        </div>
      </Inspector>
    );
  }

  const street = streetLine(customer);
  const city = cityLine(customer);

  return (
    <Inspector className="vt-detail-card">
      <Inspector.Head
        eyebrow={
          <>
            — <b>№ {customer.id_zakaznika}</b> · {customer.ico ? 'Plátce IČO' : 'Bez IČO'}
          </>
        }
        title={customer.jmeno || 'Zákazník'}
        sub={
          customer.zastupce ? (
            <span className="inline-flex items-center gap-2">
              <Pill>Zástupce</Pill>
              <span className="font-sans text-[12.5px] text-ink-2">{customer.zastupce}</span>
            </span>
          ) : null
        }
      />
      <Inspector.Body>
        <Inspector.Section heading="— Identifikace">
          <FieldRow k="IČ" v={customer.ico || '—'} mono />
          <FieldRow k="DIČ" v={customer.dico || '—'} mono />
        </Inspector.Section>
        <Inspector.Section heading="— Sídlo">
          <FieldRow k="Ulice" v={street || '—'} />
          <FieldRow k="Město" v={city || '—'} />
        </Inspector.Section>
        <Inspector.Section heading="— Kontakt">
          <FieldRow k="Telefon" v={customer.telefon || '—'} mono />
          <FieldRow k="E-mail" v={customer.email || '—'} />
        </Inspector.Section>
      </Inspector.Body>
      <Inspector.Foot>
        <Button
          variant="primary"
          size="sm"
          icon={<Icon name="external" size={12} />}
          onClick={() =>
            navigate({
              to: '/zakaznici/$id',
              params: { id: String(customer.id_zakaznika) },
            })
          }
        >
          Detail
        </Button>
      </Inspector.Foot>
    </Inspector>
  );
}
